import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { apiGet, apiPatchVoid, apiPost, apiZod } from '@/shared/api'
import {
  type CreatedMeeting,
  CreatedMeetingSchema,
  type CreateMeetingInput,
  type LobbyParticipant,
  LobbyParticipantSchema,
  type MeetingDetail,
  MeetingDetailSchema,
  type MeetingSummary,
  MeetingSummarySchema,
  type TagSuggestion,
  TagSuggestionSchema,
} from '../model/schema'
import {
  toCreatedMeeting,
  toLobbyParticipant,
  toMeetingDetail,
  toMeetingSummary,
  toTagSuggestion,
} from './mappers'

// Серверный фильтр списка; идущие (active) — отдельный запрос поверх любого фильтра.
export type MeetingListFilter = 'all' | 'scheduled' | 'active' | 'ended'

// Текстовый поиск (q — опечатко-устойчивый, по названию/описанию/тегам), точный
// фильтр по тегу и по привязке к хабу (company/project); всё комбинируется с
// filter на бэке.
export interface MeetingSearch {
  q?: string | undefined
  tag?: string | undefined
  company?: string | undefined
  project?: string | undefined
  // Только открытые клиенту компании. undefined = фильтра нет (а не «закрытые»).
  customerVisible?: boolean | undefined
}

export const meetingKeys = {
  all: ['meetings'] as const,
  lists: () => [...meetingKeys.all, 'list'] as const,
  list: (filter: MeetingListFilter, search: MeetingSearch = {}) =>
    [
      ...meetingKeys.lists(),
      filter,
      search.q ?? '',
      search.tag ?? '',
      search.company ?? '',
      search.project ?? '',
      search.customerVisible ? 'customer-visible' : '',
    ] as const,
  detail: (id: string) => [...meetingKeys.all, 'detail', id] as const,
  lobby: (id: string) => [...meetingKeys.all, 'lobby', id] as const,
}

export const tagKeys = {
  all: ['tags'] as const,
  suggest: (q: string) => [...tagKeys.all, 'suggest', q] as const,
}

export interface MeetingListPage {
  items: MeetingSummary[]
  nextCursor?: string | undefined
}

async function fetchMeetingsPage(
  filter: MeetingListFilter,
  search: MeetingSearch = {},
  cursor?: string,
): Promise<MeetingListPage> {
  const query: Record<string, string> = { filter }
  if (search.q) query.q = search.q
  if (search.tag) query.tag = search.tag
  if (search.company) query.company = search.company
  if (search.project) query.project = search.project
  if (search.customerVisible) query.customer_visible = 'true'
  if (cursor) query.cursor = cursor
  const body = await apiGet('/meetings', apiZod.zListMeetingsResponse, query)
  return {
    items: (body.data ?? []).map((dto) => MeetingSummarySchema.parse(toMeetingSummary(dto))),
    nextCursor: body.next_cursor || undefined,
  }
}

export function meetingsInfiniteQueryOptions(
  filter: MeetingListFilter,
  search: MeetingSearch = {},
) {
  return infiniteQueryOptions({
    queryKey: meetingKeys.list(filter, search),
    queryFn: ({ pageParam }) => fetchMeetingsPage(filter, search, pageParam || undefined),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? null,
  })
}

// Идущие сейчас: маленький список без пагинации — берём первую страницу.
// Срез (проект, компания, тег) обязан совпадать со срезом основного списка:
// иначе «идут сейчас» показывает не то, что обещает фильтр.
export function activeMeetingsQueryOptions(search: MeetingSearch = {}) {
  return queryOptions({
    queryKey: meetingKeys.list('active', search),
    queryFn: async (): Promise<MeetingSummary[]> =>
      (await fetchMeetingsPage('active', search)).items,
  })
}

// Подсказки тегов для автокомплита (популярные выше, опечатко-устойчиво).
export function tagSuggestionsQueryOptions(q: string) {
  return queryOptions({
    queryKey: tagKeys.suggest(q),
    queryFn: async (): Promise<TagSuggestion[]> => {
      // Канонический контракт домена tag: {items:[{name, usage_count}]}.
      const body = await apiGet('/tags', apiZod.zSuggestTagsResponse, q ? { q } : {})
      return (body.items ?? []).map((dto) => TagSuggestionSchema.parse(toTagSuggestion(dto)))
    },
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  })
}

export function meetingDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: meetingKeys.detail(id),
    queryFn: async (): Promise<MeetingDetail> => {
      const body = await apiGet(`/meetings/${id}`, apiZod.zGetMeetingResponse)
      return MeetingDetailSchema.parse(toMeetingDetail(body.data))
    },
    // Статусы записи/транскрипта/lobby приходят поллингом, пока экран открыт.
    refetchInterval: 5000,
  })
}

export function lobbyQueryOptions(id: string) {
  return queryOptions({
    queryKey: meetingKeys.lobby(id),
    queryFn: async (): Promise<LobbyParticipant[]> => {
      const body = await apiGet(`/meetings/${id}/lobby`, apiZod.zListLobbyResponse)
      return (body.data ?? []).map((dto) => LobbyParticipantSchema.parse(toLobbyParticipant(dto)))
    },
    refetchInterval: 3000,
  })
}

export function useAdmitGuestMutation(meetingId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (participantId: string) =>
      apiPost(`/meetings/${meetingId}/admit/${participantId}`, apiZod.zAdmitParticipantResponse),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: meetingKeys.lobby(meetingId) })
      void qc.invalidateQueries({ queryKey: meetingKeys.detail(meetingId) })
    },
  })
}

// Partial-редактирование: поле undefined — не менять (PATCH-семантика бэка).
// tags — replace-семантика: [] снимает все теги. companyProject — replace-привязки
// к хабу: undefined = не менять; объект = задать (оба пусто = снять привязку).
export interface UpdateMeetingInput {
  title?: string
  description?: string
  tags?: string[]
  companyProject?: { companyId: string; projectId: string }
  scheduledAt?: string
  customerVisible?: boolean
}

export function useUpdateMeetingMutation(meetingId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateMeetingInput) =>
      apiPatchVoid(`/meetings/${meetingId}`, {
        ...(input.title !== undefined ? { title: input.title } : {}),
        ...(input.description !== undefined ? { description: input.description } : {}),
        ...(input.tags !== undefined ? { tags: input.tags } : {}),
        ...(input.companyProject !== undefined
          ? {
              company_project: {
                company_id: input.companyProject.companyId,
                project_id: input.companyProject.projectId,
              },
            }
          : {}),
        ...(input.scheduledAt !== undefined ? { scheduled_at: input.scheduledAt } : {}),
        ...(input.customerVisible !== undefined ? { customer_visible: input.customerVisible } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: meetingKeys.detail(meetingId) })
      void qc.invalidateQueries({ queryKey: meetingKeys.lists() })
      void qc.invalidateQueries({ queryKey: tagKeys.all })
    },
  })
}

export function useCreateMeetingMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateMeetingInput): Promise<CreatedMeeting> => {
      const body = await apiPost('/meetings', apiZod.zCreateMeetingResponse, {
        title: input.title,
        description: input.description,
        ...(input.tags?.length ? { tags: input.tags } : {}),
        ...(input.companyId ? { company_id: input.companyId } : {}),
        ...(input.projectId ? { project_id: input.projectId } : {}),
        ...(input.invitees?.length ? { invitees: input.invitees } : {}),
        ...(input.private ? { private: true } : {}),
        ...(input.customerVisible ? { customer_visible: true } : {}),
        scheduled_at: input.scheduledAt,
      })
      return CreatedMeetingSchema.parse(toCreatedMeeting(body.data))
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: meetingKeys.lists() })
      void qc.invalidateQueries({ queryKey: tagKeys.all })
    },
  })
}
